import React from "react";
import { ClassNameProps, Conditional, Else, If, makeClassName, mappedArray, Row, StyleProps } from "@hex-insights/core";
import { settings } from "@hex-insights/forms";
import { Reforge } from "@hex-insights/uploads";
import { Image } from "../../../../Components/Image";
import { MediaLoadingPlaceholder } from "../../../../Components/LoadingPlaceholder";
import { VideoPreview } from "../../../../Components/Video";
import {
	MediaAttachment,
	reforgeSizeMultiple,
	truncateDisplayItems,
	useDynamicTruncation,
} from "../../../../Utilities";
import { MediaAttachmentFormValues } from "../../../FormUtilities";
import { MediaAttachmentUtilities } from "../../../Utilities";
import { visualAttachmentCarouselLoadingPreviewEdits } from "../VisualAttachmentCarousel";
import { preloadMediaAttachment } from "../VisualAttachmentCarousel/utils";
import styles from "./styles.module.css";

export type VisualAttachmentsRowProps = {
	attachments: Pick<MediaAttachment, "id" | "fileURL" | "fileKey" | "fileContentType">[];
	onClickAttachment?: (index: number) => void;
	size?: string;
} & Partial<ClassNameProps & StyleProps>;

export function VisualAttachmentsRow({
	attachments,
	onClickAttachment,
	size,
	className,
	style,
}: VisualAttachmentsRowProps) {
	const finalSize = size ?? (attachments.length <= 3 ? "large" : "medium");

	const ref = React.useRef<HTMLDivElement | null>(null);
	const gapSize = 12;
	const { maxDisplayed } = useDynamicTruncation(
		ref,
		(container) => container?.firstChild?.firstChild as HTMLElement | null | undefined,
		{ gapSize, syncID: attachments.length },
	);
	const {
		displayedItems: displayedAttachments,
		anyHidden,
		numHidden,
	} = truncateDisplayItems(attachments, maxDisplayed ?? 1, { includeLast: true });

	return (
		<div
			ref={ref}
			className={makeClassName(
				styles["visual-attachments-row"],
				anyHidden ? styles["visual-attachments-row--truncated"] : "",
				attachments.length === 1 ? styles["visual-attachments-row--single"] : "",
				className,
			)}
			style={style}
		>
			<Row justify="spaced-start" horizontalSpacing={`${gapSize}px`} style={{ width: "100%" }}>
				{displayedAttachments.map((attachment, i) => (
					<div key={attachment.id} className={styles["visual-attachments-row__visual-attachment__wrapper"]}>
						<If condition={anyHidden && i === displayedAttachments.length - 1}>
							<div className={styles["visual-attachments-row__visual-attachment__truncated-indicator"]}>
								<span
									className={styles["visual-attachments-row__visual-attachment__truncated-indicator__num-additional"]}
								>
									+{numHidden}
								</span>
							</div>
						</If>
						<VisualAttachment
							attachment={attachment}
							size={finalSize}
							onClick={onClickAttachment ? () => onClickAttachment(i) : undefined}
						/>
					</div>
				))}
			</Row>
		</div>
	);
}

export type VisualAttachmentProps = Omit<VisualAttachmentViewProps, "fileURL" | "fileKey" | "fileContentType"> & {
	attachment: Pick<MediaAttachment, "fileURL" | "fileKey" | "fileContentType">;
};

export function VisualAttachment({ attachment, ...props }: VisualAttachmentProps) {
	return (
		<VisualAttachmentView
			fileURL={attachment.fileURL}
			fileKey={attachment.fileKey}
			fileContentType={attachment.fileContentType}
			{...props}
		/>
	);
}

export type VisualAttachmentPreviewProps = Omit<
	VisualAttachmentViewProps,
	"fileURL" | "fileKey" | "fileContentType"
> & {
	formValues: Pick<MediaAttachmentFormValues.Base, "fileKey" | "fileContentType">;
};

export function VisualAttachmentPreview({ formValues, ...props }: VisualAttachmentPreviewProps) {
	return (
		<VisualAttachmentView
			fileURL={settings.uploads.fileInfoToURL(formValues.fileKey, formValues.fileContentType)}
			fileKey={formValues.fileKey}
			fileContentType={formValues.fileContentType}
			{...props}
		/>
	);
}

export type VisualAttachmentViewProps = {
	fileURL: string;
	fileKey: string;
	fileContentType: string;
	size?: "medium" | "large" | string;
	imageSize?: number;
	onClick?: () => void;
} & Partial<ClassNameProps & StyleProps>;

export function VisualAttachmentView({
	fileURL,
	fileKey,
	fileContentType,
	size = "medium",
	imageSize,
	onClick,
	className,
	style,
}: VisualAttachmentViewProps) {
	const sizeValue = size === "medium" || size === "large" ? undefined : size;
	const sizeClassName = size === "medium" || size === "large" ? styles[`visual-attachment--${size}`] : "";

	const imageEdits = React.useMemo<Reforge.ImageOptions["edits"] | undefined>(() => {
		if (
			MediaAttachmentUtilities.isImage({ fileContentType }) &&
			(size === "medium" || size === "large" || !!imageSize)
		) {
			const resizedSize = ((imageSize ?? size) === "medium" ? 143 : 250) * reforgeSizeMultiple;
			return { resize: { width: resizedSize, height: resizedSize, fit: "cover" } };
		}
		return undefined;
	}, [fileContentType, size, imageSize]);

	const preload = React.useCallback(() => {
		if (!MediaAttachmentUtilities.isImage({ fileContentType })) {
			return;
		}
		preloadMediaAttachment({ fileKey, fileContentType }, visualAttachmentCarouselLoadingPreviewEdits);
	}, [fileKey, fileContentType]);

	return (
		<div
			onClick={onClick}
			onMouseEnter={preload}
			className={makeClassName(
				styles["visual-attachment__container"],
				onClick ? styles["visual-attachment__container--clickable"] : "",
				className,
			)}
			style={style}
		>
			<div className={styles["visual-attachment--hover-focus__overlay"]}></div>
			<Conditional>
				<If condition={MediaAttachmentUtilities.isVideo({ fileContentType })}>
					<VideoPreview
						src={fileURL}
						sizing="cover"
						className={makeClassName(styles["visual-attachment"], sizeClassName)}
						style={{ "--visual-attachment---size": sizeValue } as React.CSSProperties}
					/>
				</If>
				<Else>
					<Image
						fileKey={fileKey}
						contentType={fileContentType}
						edits={imageEdits}
						sizing="cover"
						className={makeClassName(styles["visual-attachment"], sizeClassName)}
						loadingContent={
							<VisualAttachmentLoadingPlaceholder className={styles["visual-attachment__loading-content"]} />
						}
						style={{ "--visual-attachment---size": sizeValue } as React.CSSProperties}
					/>
				</Else>
			</Conditional>
		</div>
	);
}

export type VisualAttachmentRowLoadingPlaceholderProps = {
	numAttachments?: number;
	size?: string;
} & Partial<ClassNameProps & StyleProps>;

export function VisualAttachmentRowLoadingPlaceholder({
	numAttachments = 3,
	size,
	className,
	style,
}: VisualAttachmentRowLoadingPlaceholderProps) {
	const finalSize = size ?? (numAttachments <= 3 ? "large" : "medium");

	const ref = React.useRef<HTMLDivElement | null>(null);
	const gapSize = 12;
	const { maxDisplayed } = useDynamicTruncation(
		ref,
		(container) => container?.firstChild?.firstChild as HTMLElement | null | undefined,
		{ gapSize, syncID: numAttachments },
	);
	const attachments = React.useMemo(() => mappedArray(numAttachments, (i) => i), [numAttachments]);
	const { displayedItems: displayedAttachments, anyHidden } = truncateDisplayItems(attachments, maxDisplayed ?? 1, {
		includeLast: true,
	});

	return (
		<div
			ref={ref}
			className={makeClassName(
				styles["visual-attachments-row"],
				anyHidden ? styles["visual-attachments-row--truncated"] : "",
				attachments.length === 1 ? styles["visual-attachments-row--single"] : "",
				className,
			)}
			style={style}
		>
			<Row justify="spaced-start" horizontalSpacing={`${gapSize}px`} style={{ width: "100%" }}>
				{displayedAttachments.map((key) => (
					<VisualAttachmentLoadingPlaceholder key={key} size={finalSize} />
				))}
			</Row>
		</div>
	);
}

export type VisualAttachmentLoadingPlaceholderProps = {
	size?: "medium" | "large" | string;
} & Partial<ClassNameProps & StyleProps>;

export function VisualAttachmentLoadingPlaceholder({
	size = "medium",
	className,
	style,
}: VisualAttachmentLoadingPlaceholderProps) {
	const sizeValue = size === "medium" || size === "large" ? undefined : size;
	const sizeClassName =
		size === "medium" || size === "large" ? styles[`visual-attachment-loading-placeholder--${size}`] : "";

	return (
		<MediaLoadingPlaceholder
			className={makeClassName(styles["visual-attachment-loading-placeholder"], sizeClassName, className)}
			style={{ "--visual-attachment---size": sizeValue, ...style } as React.CSSProperties}
		></MediaLoadingPlaceholder>
	);
}
